import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { FileText, Eye, Download, HardDrive, TrendingUp, Loader2 } from "lucide-react"
import { api, type Statistics } from "@/lib/api"
import { toast } from "sonner"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export default function StatsPage() {
  const [stats, setStats] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStatistics()
  }, [])

  const fetchStatistics = async () => {
    setLoading(true)
    try {
      const data = await api.getStatistics()
      setStats(data)
    } catch (error) {
      console.error("Failed to fetch statistics:", error)
      toast.error("加载统计数据失败")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">无法加载统计数据</p>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-4 lg:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Overview Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">试卷总数</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.overview.total_papers}</div>
              <p className="text-xs text-muted-foreground">
                本月新增 {stats.overview.papers_this_month} 份
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">总浏览量</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.overview.total_views.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                平均 {stats.overview.total_papers > 0 ? Math.round(stats.overview.total_views / stats.overview.total_papers) : 0} 次/试卷
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">总下载量</CardTitle>
              <Download className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.overview.total_downloads.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                平均 {stats.overview.total_papers > 0 ? Math.round(stats.overview.total_downloads / stats.overview.total_papers) : 0} 次/试卷
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">存储空间</CardTitle>
              <HardDrive className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatBytes(stats.overview.total_storage)}</div>
              <p className="text-xs text-muted-foreground">
                平均 {stats.overview.total_papers > 0 ? formatBytes(stats.overview.total_storage / stats.overview.total_papers) : "0 B"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">本月上传</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.overview.papers_this_month}</div>
              <p className="text-xs text-muted-foreground">
                {stats.overview.total_papers > 0
                  ? ((stats.overview.papers_this_month / stats.overview.total_papers) * 100).toFixed(1)
                  : 0}% 占比
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Year Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>年份分布</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.by_year}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8" name="试卷数量" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Region Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>地区分布 (Top 5)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.by_region.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={120}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.length > 10 ? value.substring(0, 10) + '...' : value}
                  />
                  <Tooltip />
                  <Bar dataKey="value" fill="#82ca9d" name="试卷数量" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Top Papers Tables */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Top Viewed */}
          <Card>
            <CardHeader>
              <CardTitle>浏览排行榜 (Top 10)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">排名</TableHead>
                      <TableHead>试卷标题</TableHead>
                      <TableHead className="w-20">科目</TableHead>
                      <TableHead className="w-20 text-right">浏览量</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.top_viewed.map((paper, index) => (
                      <TableRow key={paper.id}>
                        <TableCell className="font-medium">
                          {index === 0 && "🥇"}
                          {index === 1 && "🥈"}
                          {index === 2 && "🥉"}
                          {index > 2 && index + 1}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm">{paper.title}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {paper.subject}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{paper.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {stats.top_viewed.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">暂无数据</p>
              )}
            </CardContent>
          </Card>

          {/* Top Downloaded */}
          <Card>
            <CardHeader>
              <CardTitle>下载排行榜 (Top 10)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">排名</TableHead>
                      <TableHead>试卷标题</TableHead>
                      <TableHead className="w-20">科目</TableHead>
                      <TableHead className="w-20 text-right">下载量</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.top_downloaded.map((paper, index) => (
                      <TableRow key={paper.id}>
                        <TableCell className="font-medium">
                          {index === 0 && "🥇"}
                          {index === 1 && "🥈"}
                          {index === 2 && "🥉"}
                          {index > 2 && index + 1}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm">{paper.title}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {paper.subject}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{paper.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {stats.top_downloaded.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">暂无数据</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tag Statistics and Recent Uploads */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Tag Cloud */}
          <Card>
            <CardHeader>
              <CardTitle>热门标签 (Top 20)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {stats.tag_stats.map((tag, _index) => (
                  <Badge
                    key={tag.name}
                    variant="secondary"
                    className="bg-primary/10 text-primary hover:bg-primary/20"
                    style={{
                      fontSize: `${Math.max(0.75, Math.min(1.25, tag.value / 10))}rem`,
                    }}
                  >
                    {tag.name} ({tag.value})
                  </Badge>
                ))}
              </div>
              {stats.tag_stats.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">暂无标签数据</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Uploads */}
          <Card>
            <CardHeader>
              <CardTitle>最近上传</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recent_uploads.map((paper) => (
                  <div key={paper.id} className="flex items-center justify-between border-b pb-2 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium">{paper.title}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(paper.created_at)}</p>
                    </div>
                    <Badge variant="outline" className="ml-2 flex-shrink-0 text-xs">
                      {paper.subject}
                    </Badge>
                  </div>
                ))}
              </div>
              {stats.recent_uploads.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">暂无上传记录</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
